/**
 * 1.5.6 — the app lives at https://scheduler.abfselis.org. One base URL (APP_BASE_URL, Admin → Settings → System,
 * "The app's address") for everything the app writes down: the tenant and volunteer /link/… pages, the opt-out
 * link, the emails' buttons and logo, the test email, the Tech release PDF footer, Help & Support, the manifest.
 * No link is ever built from window.location, the request's Host header or the old Azure address.
 *
 * The old address (Azure keeps it) still serves the app; the app shell, and the API for a page opened directly,
 * hand people on to the same path on the new one. Signed /link/… tokens carry no host, so links already sent
 * keep working on either address.
 */

export const APP_BASE_URL_KEY = 'APP_BASE_URL';

/** GET /api/app/address */
export interface AppAddressResponse {
  baseUrl: string;
  host: string;
}

export const DEFAULT_APP_BASE_URL = 'https://scheduler.abfselis.org';

/** The old Azure address ends with this (assembled, so no build output carries the old host as text). */
const OLD_HOST_SUFFIX = ['', 'azurestaticapps', 'net'].join('.');

const LOCAL = /^(localhost|127\.0\.0\.1|\[::1\])$/i;

/** True for the Static Web App's own Azure address (any host under the azurestaticapps domain). */
export function isOldHost(hostname: string): boolean {
  const h = hostname.trim().toLowerCase().replace(/:\d+$/, '');
  return h.endsWith(OLD_HOST_SUFFIX) || h === OLD_HOST_SUFFIX.slice(1);
}

/** "https://host[:port]" — https only (http only for this PC); never the old Azure address; null when not usable. */
export function normalizeBaseUrl(value: string | null | undefined): string | null {
  const v = String(value ?? '').trim();
  if (!v || v === 'Not set') return null;
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && !(u.protocol === 'http:' && LOCAL.test(u.hostname))) return null;
  if (!u.hostname || u.username || u.password || isOldHost(u.hostname)) return null;
  return u.origin;
}

/** The address to use: the Settings row, else the Azure app setting, else https://scheduler.abfselis.org. */
export function resolveAppBaseUrl(setting: string | null | undefined, envValue: string | null | undefined): string {
  return normalizeBaseUrl(setting) ?? normalizeBaseUrl(envValue) ?? DEFAULT_APP_BASE_URL;
}

/** A link on the app: the base and a path ("/link/…", "help"). */
export function appLink(base: string, path: string): string {
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

/** "scheduler.abfselis.org" */
export function hostOf(base: string): string {
  try {
    return new URL(base).host;
  } catch {
    return base.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  }
}

/**
 * Where a page opened on the old Azure address should go: the same path, query and fragment on the app's address.
 * Null on any other host, and for the sign-in paths (/.auth/*), which are left alone.
 */
export function newAddressFor(base: string, loc: { hostname: string; pathname: string; search?: string; hash?: string }): string | null {
  if (!isOldHost(loc.hostname)) return null;
  if (loc.pathname.startsWith('/.auth/') || loc.pathname === '/.auth') return null;
  return `${base.replace(/\/+$/, '')}${loc.pathname || '/'}${loc.search ?? ''}${loc.hash ?? ''}`;
}
